import { X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { AccentButton } from '../../../../../../shared/Buttons';
import { Entity } from '../../../../../../types/apiTypes';
import useApi from '../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../utils/UserContext';

export const InviteReferralModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { userRecord, userId } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);
  const [referralEmail, setReferralEmail] = useState('');
  const [referralLink, setReferralLink] = useState('');
  useEffect(() => {
    if (
      userRecord &&
      (userRecord?.userType === Entity.Vendor ||
        userRecord?.userType === Entity.Partner ||
        userRecord?.userType === Entity.PartnerAgent) &&
      userRecord?.referral_code &&
      userRecord?.referral_code?.length > 0
    ) {
      setReferralLink(
        `${window.location.origin}/signup?tachref=${encodeURIComponent(userRecord?.referral_code ?? '')}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRecord]);
  const sendReferralEmail = async (email: string) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/emails/referral',
        'post',
        {
          recipient_email: email,
          owner_id: userId ?? '',
          owner_type: userRecord?.userType ?? '',
        },
        'Error occurred while sending referral email invite.'
      );
      if (response?.status === HttpStatusCode.Ok) {
        onClose();
        toast.success('Referral email sent successfully.');
      }
    } catch (e: any) {
      console.error('Error occurred while sending referral email invite: ', e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard.');
    onClose();
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="h-auto max-h-[90%] w-full overflow-x-hidden rounded-[4px] bg-white sm:max-w-[95%] md:max-w-[80%] lg:max-w-[60%]">
              <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-[#EAECF0] p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Invite Referral
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={onClose}
                  weight="regular"
                  size={24}
                />
              </div>
              <div className="p-4">
                <h2 className="text-lg font-semibold text-primary">
                  Invite Carrier by Email
                </h2>
                <p className="text-sm text-gray-500">
                  Invite referral to join TACH by sending them an invitation
                  email.
                </p>
                <label
                  htmlFor="email"
                  className="mt-4 block text-sm text-gray-500"
                >
                  Email
                </label>
                <div className="mt-2 flex items-center space-x-2">
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter Referral Email"
                    value={referralEmail}
                    onChange={(e) => setReferralEmail(e.target.value)}
                    className="flex-1 rounded-lg border bg-gray-100 p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <AccentButton
                    onClick={() => sendReferralEmail(referralEmail)}
                    addClass="rounded-full w-[82px] h-[48px] flex items-center justify-center align-middle"
                  >
                    {!loading ? (
                      <p>Invite</p>
                    ) : (
                      <ClipLoader
                        color="#1D213B"
                        loading={loading}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                        className="flex h-full w-full justify-center"
                      />
                    )}
                  </AccentButton>
                </div>
              </div>

              {/* Share Your Unique Link Section */}
              {referralLink && referralLink?.length > 0 ? (
                <div className="p-4">
                  <h2 className="text-lg font-semibold text-primary">
                    Share Your Unique Link
                  </h2>
                  <p className="text-sm text-gray-500">
                    Invite referral to join TACH by sending them an invitation
                    link.
                  </p>
                  <label
                    htmlFor="referralLink"
                    className="mt-4 block text-sm text-gray-500"
                  >
                    Referral Link
                  </label>
                  <div className="mt-2 flex items-center space-x-2">
                    <input
                      type="text"
                      id="referralLink"
                      value={referralLink}
                      readOnly
                      className="flex-1 rounded-lg border bg-gray-200 p-3 text-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                    <button
                      onClick={() => handleCopy()}
                      className="flex h-[48px] w-[82px] items-center justify-center rounded-full border-[1px] border-[#EAECF0] bg-primarybg align-middle text-primary"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              ) : (
                <ClipLoader
                  color="#1D213B"
                  loading={!referralLink || referralLink?.length === 0}
                  size={40}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full justify-center"
                />
              )}
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return <>{renderScreenView(screenView)}</>;
};

export default InviteReferralModal;
